const _ET = 'America/New_York'

export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export function timeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now()
  if (diff <= 0) return 'expired'
  const s = Math.floor(diff / 1000)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)
  const d = Math.floor(h / 24)
  if (d > 0) return `${d}d ${h % 24}h`
  if (h > 0) return `${h}h ${m % 60}m`
  return `${m}m`
}

export function calcTimeLeft(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now()
  if (diff <= 0) return '00:00:00'
  const h = Math.floor(diff / 3_600_000)
  const m = Math.floor((diff % 3_600_000) / 60_000)
  const s = Math.floor((diff % 60_000) / 1_000)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/**
 * Returns the next midnight Eastern as a UTC ISO string — mirrors the backend's
 * daily-reset boundary (`_next_play_at`). Used to show a cooldown countdown the
 * moment a game's daily plays run out, without waiting on a server round-trip.
 * The countdown's onExpired should re-sync with the server (which is authoritative).
 */
export function nextEasternMidnightISO(): string {
  const now = new Date()
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: _ET,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(now)
  const get = (t: string) => parts.find(p => p.type === t)!.value
  const hour = get('hour') === '24' ? '00' : get('hour')
  // ET wall-clock "now", read as if UTC, to derive the current ET↔UTC offset.
  const etNow = new Date(`${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}Z`)
  const offset = now.getTime() - etNow.getTime()
  // Tomorrow's ET midnight, read as if UTC, shifted by that offset to a real instant.
  const etMidnight = new Date(`${get('year')}-${get('month')}-${get('day')}T00:00:00Z`)
  etMidnight.setUTCDate(etMidnight.getUTCDate() + 1)
  return new Date(etMidnight.getTime() + offset).toISOString()
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: _ET,
  })
}

/** e.g. "Aug 6, 2026, 3:15 AM ET" — for the newsletter's "Extract Prepared at" note. */
export function formatDateTimeET(iso: string): string {
  const formatted = new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: _ET,
  })
  return `${formatted} ET`
}

/** A plain `YYYY-MM-DD` + `HH:MM` picked in the browser, converted to a DST-aware
 *  Eastern ISO string (e.g. "2026-07-25T23:59:00-04:00"). `timeStr` must be "HH:MM" —
 *  seconds are appended here, so passing "HH:MM:SS" produces a malformed string. */
export function toEasternISO(dateStr: string, timeStr: string): string {
  const approx = new Date(`${dateStr}T${timeStr}:00Z`)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: _ET,
    timeZoneName: 'shortOffset',
  }).formatToParts(approx)
  const tzName = parts.find(p => p.type === 'timeZoneName')?.value ?? 'GMT-5'
  const match = tzName.match(/GMT([+-])(\d+)/)
  const sign = match?.[1] ?? '-'
  const hours = (match?.[2] ?? '5').padStart(2, '0')
  return `${dateStr}T${timeStr}:00${sign}${hours}:00`
}

/** Today's date in Eastern time, as `YYYY-MM-DD` (for a date input's `min`). */
export function todayET(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: _ET }).format(new Date())
}
